import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { 
  Code, Bug, Database, Server, Activity, Shield, Save,
  RefreshCw, Terminal, AlertTriangle, CheckCircle, Clock,
  Globe, ExternalLink, Check, X, Plus, Trash2, Send
} from 'lucide-react';

const DIRECTORIES = [
  { id: 'google_business', name: 'Google Business Profile', url: 'https://business.google.com', icon: '🔍' },
  { id: 'yelp', name: 'Yelp Business', url: 'https://biz.yelp.com', icon: '⭐' },
  { id: 'bing_places', name: 'Bing Places', url: 'https://www.bingplaces.com', icon: '🔎' },
  { id: 'apple_maps', name: 'Apple Maps Connect', url: 'https://mapsconnect.apple.com', icon: '🍎' },
  { id: 'yellowpages', name: 'Yellow Pages', url: 'https://www.yellowpages.com', icon: '📒' },
  { id: 'bbb', name: 'Better Business Bureau', url: 'https://www.bbb.org', icon: '🏆' },
  { id: 'healthgrades', name: 'Healthgrades', url: 'https://www.healthgrades.com', icon: '🏥' },
  { id: 'zocdoc', name: 'Zocdoc', url: 'https://www.zocdoc.com', icon: '👨‍⚕️' },
  { id: 'vitals', name: 'Vitals', url: 'https://www.vitals.com', icon: '💊' },
  { id: 'webmd', name: 'WebMD Directory', url: 'https://doctor.webmd.com', icon: '🩺' },
  { id: 'linkedin', name: 'LinkedIn Company', url: 'https://www.linkedin.com/company', icon: '💼' },
  { id: 'facebook', name: 'Facebook Business', url: 'https://www.facebook.com/business', icon: '📘' },
];

export const DevSettingsPage = () => {
  const { api, isSuperAdmin } = useAuth();
  const [settings, setSettings] = useState({
    debug_mode: false,
    log_level: 'INFO',
    api_rate_limit: 100,
    enable_webhooks: false,
    webhook_url: '',
    maintenance_mode: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [systemInfo, setSystemInfo] = useState({
    status: 'healthy',
    uptime: '99.9%',
    requests_today: 0,
    errors_today: 0,
  });
  const [directorySubmissions, setDirectorySubmissions] = useState({});
  const [customDirectories, setCustomDirectories] = useState([]);
  const [newDirectory, setNewDirectory] = useState({ name: '', url: '' });

  const fetchSettings = async () => {
    try {
      const response = await api.get('/api/settings/dev');
      setSettings({ ...settings, ...response.data });
      
      // Fetch directory submissions
      try {
        const dirRes = await api.get('/api/settings/directories');
        setDirectorySubmissions(dirRes.data?.submissions || {});
        setCustomDirectories(dirRes.data?.custom || []);
      } catch (e) {
        console.log('No directory data yet');
      }
    } catch (error) {
      console.error('Failed to fetch dev settings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/api/settings/dev', settings);
      toast.success('Dev settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleDirectory = async (directoryId, submitted) => {
    const newSubmissions = { ...directorySubmissions, [directoryId]: submitted ? new Date().toISOString() : null };
    setDirectorySubmissions(newSubmissions);
    
    try {
      await api.post('/api/settings/directories', { submissions: newSubmissions, custom: customDirectories });
      toast.success(submitted ? 'Marked as submitted' : 'Marked as not submitted');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleAddCustomDirectory = async () => {
    if (!newDirectory.name || !newDirectory.url) {
      toast.error('Please fill in both name and URL');
      return;
    }
    
    const newCustom = [...customDirectories, { 
      id: `custom_${Date.now()}`, 
      name: newDirectory.name, 
      url: newDirectory.url,
      icon: '🔗'
    }];
    setCustomDirectories(newCustom);
    setNewDirectory({ name: '', url: '' });
    
    try {
      await api.post('/api/settings/directories', { submissions: directorySubmissions, custom: newCustom });
      toast.success('Directory added');
    } catch (error) {
      toast.error('Failed to add directory');
    }
  };

  const handleRemoveCustomDirectory = async (directoryId) => {
    const newCustom = customDirectories.filter(d => d.id !== directoryId);
    setCustomDirectories(newCustom);
    
    const newSubmissions = { ...directorySubmissions };
    delete newSubmissions[directoryId];
    setDirectorySubmissions(newSubmissions);
    
    try {
      await api.post('/api/settings/directories', { submissions: newSubmissions, custom: newCustom });
      toast.success('Directory removed');
    } catch (error) {
      toast.error('Failed to remove directory');
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-600 mb-2">Access Denied</h2>
          <p className="text-gray-500">Only super admins can access dev settings</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#014DB7] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#0A1628]">Developer Settings</h1>
        <p className="text-gray-500">Advanced system configuration (Super Admin only)</p>
      </div>

      {/* Warning Banner */}
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
        <div>
          <h3 className="font-semibold text-red-700">Caution: Advanced Settings</h3>
          <p className="text-sm text-red-600">
            Changes here can affect system stability. Only modify if you understand the implications.
          </p>
        </div>
      </div>

      {/* System Status */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-5 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">System Status</p>
              <p className="text-lg font-bold text-green-600 capitalize">{systemInfo.status}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#014DB7]/10 rounded-lg flex items-center justify-center">
              <Activity className="w-5 h-5 text-[#014DB7]" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Uptime</p>
              <p className="text-lg font-bold text-[#0A1628]">{systemInfo.uptime}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#84CC16]/10 rounded-lg flex items-center justify-center">
              <Server className="w-5 h-5 text-[#84CC16]" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Requests Today</p>
              <p className="text-lg font-bold text-[#0A1628]">{systemInfo.requests_today}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Bug className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Errors Today</p>
              <p className="text-lg font-bold text-red-500">{systemInfo.errors_today}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Debug Settings */}
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
              <Bug className="w-5 h-5 text-purple-600" />
            </div>
            <h2 className="text-lg font-semibold text-[#0A1628]">Debug Settings</h2>
          </div>

          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Debug Mode</Label>
                <p className="text-sm text-gray-500">Enable verbose logging and error details</p>
              </div>
              <button
                onClick={() => setSettings({ ...settings, debug_mode: !settings.debug_mode })}
                className={`w-12 h-6 rounded-full transition-colors ${
                  settings.debug_mode ? 'bg-[#84CC16]' : 'bg-gray-300'
                }`}
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  settings.debug_mode ? 'translate-x-6' : 'translate-x-0.5'
                }`} />
              </button>
            </div>

            <div className="space-y-2">
              <Label>Log Level</Label>
              <select
                value={settings.log_level}
                onChange={(e) => setSettings({ ...settings, log_level: e.target.value })}
                className="w-full rounded-xl border border-gray-200 p-3 focus:ring-2 focus:ring-[#014DB7]/30"
              >
                <option value="DEBUG">DEBUG</option>
                <option value="INFO">INFO</option>
                <option value="WARNING">WARNING</option>
                <option value="ERROR">ERROR</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label>API Rate Limit (requests/min)</Label>
              <Input
                type="number"
                value={settings.api_rate_limit}
                onChange={(e) => setSettings({ ...settings, api_rate_limit: parseInt(e.target.value) })}
                className="rounded-xl"
              />
            </div>
          </div>
        </div>

        {/* Webhooks & Maintenance */}
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
              <Terminal className="w-5 h-5 text-orange-600" />
            </div>
            <h2 className="text-lg font-semibold text-[#0A1628]">Webhooks & System</h2>
          </div>

          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Enable Webhooks</Label>
                <p className="text-sm text-gray-500">Send events to external URLs</p>
              </div>
              <button
                onClick={() => setSettings({ ...settings, enable_webhooks: !settings.enable_webhooks })}
                className={`w-12 h-6 rounded-full transition-colors ${
                  settings.enable_webhooks ? 'bg-[#84CC16]' : 'bg-gray-300'
                }`}
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  settings.enable_webhooks ? 'translate-x-6' : 'translate-x-0.5'
                }`} />
              </button>
            </div>

            {settings.enable_webhooks && (
              <div className="space-y-2">
                <Label>Webhook URL</Label>
                <Input
                  value={settings.webhook_url || ''}
                  onChange={(e) => setSettings({ ...settings, webhook_url: e.target.value })}
                  placeholder="https://your-webhook-url.com"
                  className="rounded-xl"
                />
              </div>
            )}

            <div className="pt-4 border-t">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base text-red-600">Maintenance Mode</Label>
                  <p className="text-sm text-gray-500">Disable access for non-admin users</p>
                </div>
                <button
                  onClick={() => setSettings({ ...settings, maintenance_mode: !settings.maintenance_mode })}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    settings.maintenance_mode ? 'bg-red-500' : 'bg-gray-300'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    settings.maintenance_mode ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Database Info */}
      <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-[#014DB7]/10 rounded-xl flex items-center justify-center">
            <Database className="w-5 h-5 text-[#014DB7]" />
          </div>
          <h2 className="text-lg font-semibold text-[#0A1628]">Database Information</h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-gray-50 rounded-xl">
            <p className="text-sm text-gray-500">Database</p>
            <p className="font-semibold text-[#0A1628]">MongoDB</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-xl">
            <p className="text-sm text-gray-500">Collections</p>
            <p className="font-semibold text-[#0A1628]">12</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-xl">
            <p className="text-sm text-gray-500">Total Documents</p>
            <p className="font-semibold text-[#0A1628]">~</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-xl">
            <p className="text-sm text-gray-500">Connection</p>
            <p className="font-semibold text-green-600">Active</p>
          </div>
        </div>
      </div>

      {/* Directory Submissions */}
      <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#84CC16]/10 rounded-xl flex items-center justify-center">
              <Globe className="w-5 h-5 text-[#84CC16]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[#0A1628]">Directory Submissions</h2>
              <p className="text-sm text-gray-500">Track business listing submissions for SEO</p>
            </div>
          </div>
          <div className="text-sm text-gray-500">
            {Object.values(directorySubmissions).filter(Boolean).length} / {DIRECTORIES.length + customDirectories.length} submitted
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#84CC16] transition-all duration-500"
              style={{ 
                width: `${(Object.values(directorySubmissions).filter(Boolean).length / (DIRECTORIES.length + customDirectories.length)) * 100}%` 
              }}
            />
          </div>
        </div>

        {/* Directory Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
          {[...DIRECTORIES, ...customDirectories].map(dir => {
            const isSubmitted = !!directorySubmissions[dir.id];
            const submittedDate = directorySubmissions[dir.id];
            const isCustom = dir.id.startsWith('custom_');
            
            return (
              <div 
                key={dir.id}
                className={`p-4 rounded-xl border-2 transition-all ${
                  isSubmitted 
                    ? 'border-[#84CC16] bg-[#84CC16]/5' 
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{dir.icon}</span>
                    <div>
                      <p className="font-medium text-[#0A1628]">{dir.name}</p>
                      <a 
                        href={dir.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-[#014DB7] hover:underline flex items-center gap-1"
                      >
                        Visit <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isCustom && (
                      <button
                        onClick={() => handleRemoveCustomDirectory(dir.id)}
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleToggleDirectory(dir.id, !isSubmitted)}
                      className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                        isSubmitted 
                          ? 'bg-[#84CC16] text-white' 
                          : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                      }`}
                    >
                      {isSubmitted ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                {isSubmitted && submittedDate && (
                  <p className="text-xs text-gray-500 mt-2 pl-11">
                    Submitted: {new Date(submittedDate).toLocaleDateString()}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Add Custom Directory */}
        <div className="border-t border-gray-200 pt-4">
          <h3 className="font-medium text-[#0A1628] mb-3">Add Custom Directory</h3>
          <div className="flex gap-3">
            <Input
              placeholder="Directory Name"
              value={newDirectory.name}
              onChange={(e) => setNewDirectory({ ...newDirectory, name: e.target.value })}
              className="rounded-xl"
            />
            <Input
              placeholder="https://..."
              value={newDirectory.url}
              onChange={(e) => setNewDirectory({ ...newDirectory, url: e.target.value })}
              className="rounded-xl"
            />
            <Button 
              onClick={handleAddCustomDirectory}
              className="bg-[#014DB7] text-white rounded-xl px-6"
            >
              <Plus className="w-4 h-4 mr-2" /> Add
            </Button>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSaveSettings} disabled={saving} className="bg-[#014DB7] hover:bg-[#003A8C] text-white rounded-xl">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Dev Settings'}
        </Button>
      </div>
    </div>
  );
};

export default DevSettingsPage;
